import React, { useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { Formik, FormikProps } from 'formik';
import { useMutation } from '@apollo/client';

import { handleValidationErrors, onErrorHandler } from '@/services/apollo';
import { initializedAtom, userAtom } from '@/components/with_auth/State';
import routes from '@/services/routes';
import { UPDATE_BRIEF, UPDATE_USER } from '@/components/onboarding/Actions';
import {
    haveErrors,
    reportGeneralError,
    BIO_NAMES,
    BIO_STATES,
} from '@/components/onboarding/Shared';
import useIsEdit from '@/hooks/use_is_edit';
import PanelContainer from '@/components/commons/panel_container';
import { type bioPageProps } from '@/types/globals';

import NavButtons from './NavigationButtons';
import BioPageHeader from './BioPageHeader';
import SocialLinksForm from './SocialLinksForm';
import personalizeStyles from './Personalize.module.css';
import socialsUrlCleaner from './helpers';

function SocialLinksPanel({
    isModal = false,
    extendContinue = null,
    overrideBack = null,
    isEdit,
    openBrief,
}: bioPageProps) {
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const { url } = useIsEdit();

    const user = useRecoilValue(userAtom);
    const setters = {
        setUser: useSetRecoilState(userAtom),
        setInitialized: useSetRecoilState(initializedAtom),
    };

    const formRef = useRef<FormikProps<any>>(null);
    const [updateUser] = useMutation(UPDATE_USER);
    const [updateBrief] = useMutation(UPDATE_BRIEF);

    const briefId = openBrief ? openBrief.briefIndex : user.briefs[user.briefs.length - 1].id;
    const brief = user.briefs[
        user.briefs.findIndex(
            (briefObj) => briefObj.id === briefId,
        )
    ];

    const handleContinue = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const handleBackNavigation = () => {
        if (isModal && overrideBack) {
            overrideBack(false);
        } else {
            router.push(routes.BIO_MEETING_PREFERENCES);
        }
    };

    const handleSubmit = async (values, { setStatus }) => {
        const sanitizedUrls = socialsUrlCleaner(values);
        setIsLoading(true);
        const attributes = {
            // onboardingState: BIO_STATES.review,
            // location: frm.location,
            socialLinks: {
                ...user?.socialLinks,
                linkedIn: sanitizedUrls.linkedIn,
                xTwitter: sanitizedUrls.xTwitter,
                facebook: sanitizedUrls.facebook,
                instagram: sanitizedUrls.instagram,
                gitHub: sanitizedUrls.gitHub,
                other_1: sanitizedUrls.other_1,
                other_2: sanitizedUrls.other_2,
            },
        };

        const response = await updateBrief({
            variables: {
                input: {
                    id: briefId,
                    attributes,
                },
            },
            fetchPolicy: 'no-cache',
            // handle network errors/issues (user can't do much but retry)
            onError: onErrorHandler((error) => {
                reportGeneralError(error);
            }),
        });
        // check for issues with submission (user messed up)
        let errors = response.data?.updateBrief?.errors;
        if (haveErrors(errors)) {
            handleValidationErrors(errors, setStatus, 'brief');
            setIsLoading(false);
            return;
        }

        const userRes = await updateUser({
            variables: {
                updateUserInput: {
                    attributes: {
                        location: sanitizedUrls.location,
                        onboardingState: BIO_STATES.review,
                    },
                },
            },
            fetchPolicy: 'no-cache',
            // network errors/server errors (non-user)
            onError: onErrorHandler((error) => {
                reportGeneralError(error);
            }),
        });

        // check for user specific errors
        errors = userRes.data?.updateUser?.errors;
        if (haveErrors(errors)) {
            handleValidationErrors(errors, setStatus, 'user');
            setIsLoading(false);
            return;
        }
        setters.setUser(userRes.data?.updateUser?.user);
        setters.setInitialized(true);

        // reset state and move forward on success
        setStatus({ errors: {} });
        setIsLoading(false);
        if (url) {
            router.push(url);
        } else if (isModal && extendContinue) {
            extendContinue('', isEdit);
        } else {
            router.push(routes.BIO_REVIEW_BIO);
        }
    };

    return (
        <PanelContainer stepNames={BIO_NAMES} paddingOverride="p-0 sm:p-8" step={4} totalSteps={5} image="/images/bio/bio_personalize.png" isModal={isModal}>
            <div className="w-full py-2 px-2 sm:px-5">
                <BioPageHeader
                    header="Where can we learn more?"
                    subHeader="Share a few details about your location, as well as any social, professional, or portfolio links to help people prepare to meet you. "
                    subHeaderWidth="max-w-[677px]"
                />
                <div className="flex flex-col md:flex-row space-between gap-2 lg:gap-8">
                    <div className="w-full sm:ml-0">
                        <Formik
                            innerRef={formRef}
                            onSubmit={handleSubmit}
                            // default validate on submit and blur
                            // on change is a little obnoxious
                            validateOnChange={false}
                            initialValues={{
                                location: user?.location || '',
                                linkedIn: brief?.socialLinks?.linkedIn || user?.socialLinks?.linkedIn || '',
                                xTwitter: brief?.socialLinks?.xTwitter || user?.socialLinks?.xTwitter || '',
                                facebook: brief?.socialLinks?.facebook || user?.socialLinks?.facebook || '',
                                gitHub: brief?.socialLinks?.gitHub || user?.socialLinks?.gitHub || '',
                                instagram: brief?.socialLinks?.instagram || user?.socialLinks?.instagram || '',
                                other_1: brief?.socialLinks?.other_1 || user?.socialLinks?.other_1 || '',
                                other_2: brief?.socialLinks?.other_2 || user?.socialLinks?.other_2 || '',
                            }}
                        >
                            {/*
                                'status' is overall form status (API errors)
                                'errors' is Formik field validation specific user issues
                                because we use ErrorMessage helper from Formik, we don't
                                need to pass errors anymore
                            */}
                            {({ status }) => (
                                <SocialLinksForm
                                    status={status}
                                />
                            )}
                        </Formik>
                    </div>

                </div>
                <NavButtons
                    containerStyles={personalizeStyles.buttonGroupWithoutRelative}
                    handleBackNavigation={handleBackNavigation}
                    handleContinue={handleContinue}
                    isLoading={isLoading}
                    isEdit={isEdit || url}
                />
            </div>
        </PanelContainer>
    );
}

export default SocialLinksPanel;
